import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

const resolutionsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'resolutions' },
  op: 'query',
  resourcePath: 'api/v2/resolutions',
};

/** The command envelope's `result` for a recorded answer. */
export interface ServicerAnswerResult {
  /** False when the same servicer was already applied — a replay, not a second write. */
  applied: boolean;
  /** The member's account the answer created or attached, as a string id; null on a replay. */
  accountId: string | null;
}

/**
 * POST /api/v2/resolutions/:id/answer — the signed-in member answers the Servicer
 * question opened on one of their own uploads. Body `{ document_id,
 * financial_institution_id }` in snake_case, as the command controller reads it.
 *
 * Applies at once to that member's own document and account; the shared question
 * stays open for ops. Read the result with `parseCommandResult<ServicerAnswerResult>`.
 *
 * @throws 409 when the document already carries a different applied servicer — the
 *   caller is acting on state it has not seen
 * @throws 404 when the question is closed (ops rejected it), the document is not the
 *   caller's or no longer waits on it, or the servicer is not an active, public one
 */
export function answerServicerQuestionRequest(
  resolutionId: string,
  answer: { documentId: string; financialInstitutionId: string }
): RequestInfo {
  return {
    url: `${buildBaseURL(resolutionsUrlOptions)}/${resolutionId}/answer`,
    method: 'POST',
    body: JSON.stringify({
      document_id: answer.documentId,
      financial_institution_id: answer.financialInstitutionId,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}
